import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GetSetupWizardAPI,
  SaveSetupAreaAPI,
  SaveSetupIdentityAPI,
  SeedDemoDataAPI,
  TrackCompanySetupStepAPI,
  UpdateCompanySetupStatusAPI,
} from '../../../api/API';
import { DataProviderInstance } from '../../../api/DataProvider';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import { SETUP_STEPS } from '../constants/setupSteps';

const SAVE_APIS = {
  area: SaveSetupAreaAPI,
  identity: SaveSetupIdentityAPI,
};

const LAST_STEP = SETUP_STEPS.length - 1;

/** Exemplos criados e ainda na conta: a área fica como está (save_area.php recusa trocar). */
const hasExamples = (data) => !!data?.example?.seeded_at && !data?.example?.removed_at;

/**
 * Estado e navegação da configuração inicial v2.
 * Os dados chegam prontos de get_wizard.php; cada etapa salva no próprio
 * endpoint e só avança quando o backend confirma.
 */
export default function useSetupWizard() {
  const navigate = useNavigate();
  // As etapas gravam (direto ou pelos padrões da área) horários, procedimentos, profissional e tipos de atendimento: ao sair,
  // recarrega a configuração da clínica para a agenda e o atendimento não usarem a de antes
  const { GetCompany, GetPatients } = DataProviderInstance();
  const { isInActiveTrial } = useSubscription();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [finished, setFinished] = useState(false);
  // Exemplos da área: pedidos em segundo plano ao salvar o passo 2 (seedRunning até o servidor
  // responder, seedDone depois); "Estamos preparando sua conta" só aparece se concluir antes disso
  const [preparing, setPreparing] = useState(false);
  const [seedRunning, setSeedRunning] = useState(false);
  const [seedDone, setSeedDone] = useState(false);
  const seedStartedRef = useRef(false);
  const trackedRef = useRef(null);

  const load = useCallback(() => {
    setLoadError(false);
    GetSetupWizardAPI((response) => {
      if (!response || !response.success) {
        setLoadError(true);
        return;
      }
      if (response.status === 'INITIAL') {
        UpdateCompanySetupStatusAPI('IN_PROGRESS', () => {});
      }
      // A página caiu (ou recarregou) depois de criar os exemplos e antes de concluir:
      // a pessoa já tinha passado do passo 2, então volta direto ao teste no WhatsApp
      if (response.status !== 'COMPLETE' && hasExamples(response)) {
        setStepIndex(LAST_STEP);
      }
      setData(response);
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  // Etapa exibida, para o card "Configuração inicial" do Admin Geral
  useEffect(() => {
    if (!data || finished) return;
    const key = SETUP_STEPS[stepIndex].key;
    if (trackedRef.current === key) return;
    trackedRef.current = key;
    TrackCompanySetupStepAPI(key, () => {});
  }, [data, stepIndex, finished]);

  const goTo = useCallback((index) => {
    setError(null);
    setStepIndex(Math.max(0, Math.min(index, LAST_STEP)));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const back = useCallback(() => goTo(stepIndex - 1), [goTo, stepIndex]);
  const next = useCallback(() => goTo(stepIndex + 1), [goTo, stepIndex]);

  /**
   * Exemplos da área (docs/features/trial-demo-data, seção 3), só na primeira configuração
   * de uma conta em teste. Pedidos uma vez só (o ref segura o passo 2 salvo de novo e o
   * StrictMode); o servidor também só cria uma vez (ALREADY_SEEDED conta como pronto).
   * Em erro, a configuração conclui sem exemplos.
   */
  const startSeed = useCallback(() => {
    if (seedStartedRef.current) return;
    if (data?.status === 'COMPLETE' || data?.example?.seeded_at || !isInActiveTrial()) return;
    seedStartedRef.current = true;
    setSeedRunning(true);
    setSeedDone(false);
    SeedDemoDataAPI((response) => {
      if (response?.success || response?.code === 'ALREADY_SEEDED') {
        setData((current) => ({
          ...current,
          example: {
            ...current.example,
            seeded_at: response.demo_data?.seeded_at || new Date().toISOString(),
            removed_at: response.demo_data?.removed_at || null,
            counts: response.counts || null,
          },
        }));
        // A lista de pessoas em memória foi carregada antes dos exemplos existirem:
        // sem recarregar, a agenda mostraria os blocos sem nome nem selo Exemplo
        GetPatients(false);
      }
      setSeedRunning(false);
      setSeedDone(true);
    });
  }, [data, isInActiveTrial, GetPatients]);

  /** Salva a etapa atual e avança. `merge` atualiza o pedaço de `data` da etapa. */
  const save = useCallback((stepKey, payload, merge) => {
    setSaving(true);
    setError(null);
    SAVE_APIS[stepKey](payload, (response) => {
      setSaving(false);
      if (!response || !response.success) {
        setError({
          message: response?.error || 'Não foi possível salvar. Tente de novo.',
          fields: response?.errors || {},
          code: response?.code || null,
        });
        return;
      }
      setData((current) => ({ ...current, [stepKey]: merge ? merge(response) : payload }));
      // Com o passo 2 salvo, os exemplos já são montados enquanto a pessoa faz o teste no WhatsApp
      if (stepKey === 'identity') startSeed();
      goTo(stepIndex + 1);
    });
  }, [goTo, stepIndex, startSeed]);

  const complete = useCallback(() => {
    setSaving(true);
    UpdateCompanySetupStatusAPI('COMPLETE', () => {
      GetCompany();
      setSaving(false);
      setPreparing(false);
      setFinished(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }, [GetCompany]);

  /**
   * Concluir: com os exemplos prontos (ou sem exemplo para criar), vai direto para o fim.
   * Ainda sendo criados: "Estamos preparando sua conta" espera seedDone e chama complete().
   * Quem chegou aqui sem salvar o passo 2 nesta visita começa os exemplos agora.
   */
  const finish = useCallback(() => {
    if (!seedStartedRef.current) startSeed();
    if (!seedStartedRef.current || seedDone) {
      complete();
      return;
    }
    setPreparing(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [seedDone, startSeed, complete]);

  /** "Fazer depois": não volta a abrir sozinho; o card Primeiros passos traz de volta. */
  const skip = useCallback(() => {
    if (data?.status === 'COMPLETE') {
      navigate('/dashboard');
      return;
    }
    UpdateCompanySetupStatusAPI('SKIPPED', () => {
      GetCompany();
      navigate('/dashboard');
    });
  }, [navigate, data, GetCompany]);

  const setDemoState = useCallback((state) => {
    setData((current) => ({ ...current, demo: { ...current.demo, state } }));
  }, []);

  return {
    data,
    loadError,
    reload: load,
    stepIndex,
    stepKey: SETUP_STEPS[stepIndex].key,
    saving,
    error,
    finished,
    preparing,
    seedDone,
    // A área fica travada enquanto os exemplos são criados e depois que existem
    areaLocked: seedRunning || hasExamples(data),
    complete,
    back,
    next,
    save,
    finish,
    skip,
    setDemoState,
  };
}
